<template>
  <div class="map" :id="mapId" ref="mapRef"></div>
</template>

<script setup lang="ts">
import { datadogRum } from '@datadog/browser-rum';
import { v4 as uuid } from 'uuid';
import { onMounted, ref, watch } from 'vue';

import { getGoogleMapsLoader } from '../../configs/google-api.config';
import { Coordinates } from './coordinates';

export interface MapProps {
  coordinates?: Coordinates;
}

const defaultCoordinates: Coordinates = {
  latitude: 39.8283,
  longitude: -98.5795,
};

const mapId = `map-${uuid()}`;
const mapRef = ref<HTMLElement>();
const props = defineProps<MapProps>();
let map: google.maps.Map;
let marker: google.maps.marker.AdvancedMarkerElement;

onMounted(() => {
  initializeMap().catch(reportMapError);
});

watch(
  () => props.coordinates,
  (newCoords?: Coordinates) => {
    if (newCoords && newCoords.latitude && newCoords.longitude) {
      updateMapPosition(newCoords).catch(reportMapError);
    }
  },
);

// Google Maps is a cross-origin script, so when it throws inside these promise chains the browser
// reports nothing at all (Chrome fires neither onerror nor onunhandledrejection for it). Reporting
// here keeps the real message and stack.
function reportMapError(error: unknown): void {
  datadogRum.addError(error);
}

async function updateMapPosition(coordinates: Coordinates): Promise<void> {
  if (!map || !marker) {
    return;
  }

  const position = new google.maps.LatLng(coordinates.latitude, coordinates.longitude);
  map.setCenter(position);
  map.setZoom(15);
  marker.position = position;
}

async function initializeMap(): Promise<void> {
  const { Map } = await getGoogleMapsLoader().importLibrary('maps');
  const { AdvancedMarkerElement } = await getGoogleMapsLoader().importLibrary('marker');

  // The component can unmount while the Google Maps libraries above are loading
  // (e.g. the user navigates away mid-init), which resets the template ref to null.
  // Bail instead of handing Google Maps a null container — that throws InvalidValueError.
  if (!mapRef.value) {
    return;
  }

  let position: google.maps.LatLng;
  let zoom: number;

  if (!props.coordinates || !props.coordinates.latitude || !props.coordinates.longitude) {
    position = new google.maps.LatLng(defaultCoordinates.latitude, defaultCoordinates.longitude);
    zoom = 2;
  } else {
    position = new google.maps.LatLng(props.coordinates.latitude, props.coordinates.longitude);
    zoom = 15;
  }

  map = new Map(mapRef.value, {
    mapId,
    zoom,
    center: position,
    disableDefaultUI: true,
    gestureHandling: 'none',
    clickableIcons: false,
  });

  const iconImage = document.createElement('img');
  iconImage.src = '/assets/map-pin.svg';
  iconImage.width = 96;
  iconImage.height = 96;
  iconImage.style.transform = 'translateY(50%)';

  marker = new AdvancedMarkerElement({
    map,
    position: map.getCenter(),
    content: iconImage,
  });
}
</script>

<style scoped lang="scss">
.map {
  width: 100%;
  aspect-ratio: 1 / 0.5;
  border-radius: var(--t-border-radius-sm);
}
</style>
